<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  allVariables: { type: Array, default: () => [] },
  sampleVariables: { type: Object, default: () => ({}) },
  variableTypes: { type: Object, default: () => ({}) },
  headerText: { type: String, default: '' },
  bodyText: { type: String, default: '' },
  footerText: { type: String, default: '' },
});

const emit = defineEmits(['update:sampleVariables', 'update:variableTypes']);

const { t } = useI18n();

const headerVars = computed(() =>
  props.allVariables.filter(v => props.headerText.includes(`{{${v}}}`))
);
const bodyVars = computed(() =>
  props.allVariables.filter(v => props.bodyText.includes(`{{${v}}}`))
);
const footerVars = computed(() =>
  props.allVariables.filter(v => props.footerText.includes(`{{${v}}}`))
);

const variableGroups = computed(() =>
  [
    {
      key: 'header',
      title: t('INBOX_MGMT.TEMPLATES.CREATE.HEADER_TYPE'),
      vars: headerVars.value,
    },
    {
      key: 'body',
      title: t('INBOX_MGMT.TEMPLATES.CREATE.BODY'),
      vars: bodyVars.value,
    },
    {
      key: 'footer',
      title: t('INBOX_MGMT.TEMPLATES.CREATE.FOOTER'),
      vars: footerVars.value,
    },
  ].filter(group => group.vars.length > 0)
);

const updateSample = (varNum, val) => {
  emit('update:sampleVariables', { ...props.sampleVariables, [varNum]: val });
};

const updateType = (varNum, val) => {
  emit('update:variableTypes', { ...props.variableTypes, [varNum]: val });
};

// Must not use '{{' inside mustaches — Vue tokenizer misreads it as interpolation
const varLabel = n => `\u007b\u007b${n}\u007d\u007d`;
</script>

<template>
  <div v-if="allVariables.length > 0" class="space-y-4">
    <div>
      <p class="text-xs text-n-slate-10">
        {{ t('INBOX_MGMT.TEMPLATES.CREATE.VARIABLE_SAMPLES_DESC') }}
      </p>
    </div>

    <div v-for="group in variableGroups" :key="group.key" class="space-y-2">
      <h5
        class="text-[11px] font-semibold uppercase tracking-wide text-n-slate-9"
      >
        {{ group.title }}
      </h5>
      <div
        v-for="varNum in group.vars"
        :key="`${group.key}-${varNum}`"
        class="grid grid-cols-[auto_1fr_1fr] gap-2 items-center"
      >
        <span
          class="text-xs font-mono bg-n-slate-3 text-n-slate-11 px-2 py-1 rounded"
        >
          {{ varLabel(varNum) }}
        </span>
        <select
          :value="variableTypes[varNum] || 'text'"
          class="h-8 px-2 rounded-lg outline outline-1 outline-n-weak bg-n-background text-xs text-n-slate-12 focus:outline-n-brand"
          @change="updateType(varNum, $event.target.value)"
        >
          <option value="text">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_TEXT') }}
          </option>
          <option value="number">
            {{ t('INBOX_MGMT.TEMPLATES.CREATE.VAR_TYPE_NUMBER') }}
          </option>
        </select>
        <input
          :value="sampleVariables[varNum] || ''"
          type="text"
          class="h-8 px-2 rounded-lg outline outline-1 outline-n-weak bg-transparent text-xs text-n-slate-12 focus:outline-n-brand"
          :placeholder="t('INBOX_MGMT.TEMPLATES.CREATE.SAMPLE_VALUE')"
          @input="updateSample(varNum, $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>
